import React, { FC, useEffect, useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom';
import { ExtendedTheme } from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { isInViewport } from '../../utils/element-position.utils';
import { useLogger } from '../providers/logger/Logger'
import { LogAction, LogLevel } from '../providers/logger/logger-types'
import { Article, PromotionalContentSummary } from '../../shared/types'
import { useUser } from '../providers/UserProvider';
import { baseUrls } from '../../utils/baseUrls.utils';

interface PromotionalContentSidebarProps {
	article: Article;
}

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	image: {
		display: 'block',
		width: '100%',
		borderRadius: 8,
		transition: 'opacity .3s ease-out',
		'&:hover': {
			opacity: 0.8
		},
		[theme.breakpoints.down('sm')]: {
			maxWidth: '400px'
		}
	},
	link: {
		display: 'block'
	}
}));

export const PromotionalContentSidebar: FC<PromotionalContentSidebarProps> = ({ article: { randomisedPromotionalContent, title, _id}}) => {
	const classes = useStyles();

	const [mountedTimestamp, setMountedTimestamp] = useState(0);

	const offsetElement = useRef<HTMLAnchorElement>(null);

	const { printLog } = useLogger()

	const { userDetails } = useUser()

	const {_id: id, link, name, sidebarImage} = randomisedPromotionalContent as PromotionalContentSummary;

	const isInViewportHandler = useCallback(() => {
		if (isInViewport<HTMLAnchorElement>(offsetElement.current)) {
			printLog(
				LogLevel.INFO,
				LogAction.SideBarImpression,
				'Promotional Content',
				{
					name: name,
					id: id,
					articleName: title,
					articleId: _id,
					userId: userDetails?.id,
					whenMs: Date.now() - mountedTimestamp
				}
			);
			window.removeEventListener('scroll', isInViewportHandler, false);
		}
	}, [printLog, name, id, title, _id, userDetails, mountedTimestamp]);

	const clickHandler = useCallback(({currentTarget}) => {
		const promotionalContentName = currentTarget.getAttribute('data-promotional-name')
		const promotionalContentId = currentTarget.getAttribute('data-promotional-id')

		printLog(
			LogLevel.INFO,
			LogAction.SideBarClick,
			'Promotional Content',
			{
				name: promotionalContentName,
				id: promotionalContentId,
				articleName: title,
				articleId: id,
				userId: userDetails?.id,
				whenMs: Date.now() - mountedTimestamp
			}
		);
	}, [id, mountedTimestamp, printLog, title, userDetails]);

	useEffect(() => {
		const currentOffsetElement = offsetElement.current;

		currentOffsetElement?.addEventListener('click', clickHandler)

		return () => currentOffsetElement?.removeEventListener('click', clickHandler, false);
	}, [clickHandler])

	useEffect(() => {
		isInViewportHandler()
		window.addEventListener('scroll', isInViewportHandler)
		return () => window.removeEventListener('scroll', isInViewportHandler, false);
	}, [isInViewportHandler])

	useEffect(() => {
		setMountedTimestamp(Date.now())
	}, [])

	return (
		<div>
			{link.length > 0 ? (
				<a ref={offsetElement} target="_blank" href={link} data-promotional-name={name} data-promotional-id={id} rel="noopener noreferrer" aria-label={name} className={classes.link}>
					<img src={sidebarImage} alt={name} className={classes.image} />
				</a>
			) : (
				<Link innerRef={offsetElement} to={`${baseUrls.promotion.replace(':_id', id)}`} data-promotional-name={name} data-promotional-id={id} rel="noopener noreferrer" aria-label={name} className={classes.link}>
					<img src={sidebarImage} alt={name} className={classes.image} />
				</Link>
			)}
		</div>
	);
};

export default PromotionalContentSidebar;
